import { Button, Card, CardDescription, CardTitle, StatusBadge } from "../ui/index.js";
import {
  SurelcReadinessList,
  SurelcRequestList,
  type SurelcOnboardingData,
  type SurelcStepStatus,
} from "../surelc/SurelcStatusLists.js";

export type SurelcOnboardingSummary = SurelcOnboardingData;

function stepMark(status: SurelcStepStatus) {
  if (status === "complete") return "✓";
  if (status === "current") return "→";
  if (status === "pending") return "◷";
  return "–";
}

/**
 * "Here's what to know" under the Start contracting box (Paul, Oct 28): what
 * comes next, without Altura's own back-office steps. The call to action for
 * picking carriers lives in the box above, so the carrier-selection hint is
 * not repeated here.
 */
export function SurelcOnboardingCard({
  onboarding,
  opening,
  proceeding,
  error,
  onOpen,
  onProceed,
}: {
  onboarding: SurelcOnboardingSummary;
  opening: boolean;
  proceeding: boolean;
  error: string | null;
  onOpen: () => void;
  onProceed: () => void;
}) {
  const canOpen = Boolean(onboarding.producerId && onboarding.ssoReady);
  const missing = onboarding.readiness.filter((item) => item.status === "missing").length;
  const showProceed = onboarding.canProceed && !onboarding.proceededAt;
  const steps = onboarding.steps.filter(
    (step) =>
      step.owner !== "altura" &&
      (step.id !== "proceed" || onboarding.proceedRequired || onboarding.canProceed || onboarding.proceededAt)
  );
  const showNextAction = onboarding.stage !== "identity" && onboarding.stage !== "carrier_selection";
  return (
    <Card padding="lg" className="space-y-5 border-2">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle>Here&apos;s what to know</CardTitle>
            {onboarding.staffAttention || missing > 0 ? (
              <StatusBadge status="NIGO" size="sm" />
            ) : onboarding.producerId ? (
              <StatusBadge status="ACTIVE" size="sm" />
            ) : (
              <StatusBadge status="PENDING" size="sm" />
            )}
          </div>
          {showNextAction ? (
            <CardDescription className="mt-1">{onboarding.nextAction}</CardDescription>
          ) : (
            <CardDescription className="mt-1">
              After you submit, you finish the carrier paperwork in SureLC.
            </CardDescription>
          )}
          {onboarding.mode === "dry_run" ? (
            <p className="mt-2 text-xs font-medium text-status-pending-text">
              Altura is testing this connection. No paid SureLC producer was created.
            </p>
          ) : onboarding.mode === "simulated" ? (
            <p className="mt-2 text-xs font-medium text-status-pending-text">
              Test mode: this SureLC account is simulated. Nothing was sent to SureLC, and the progress below is made up.
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {showProceed ? (
            <Button onClick={onProceed} disabled={proceeding}>
              {proceeding ? "Unlocking…" : "Proceed"}
            </Button>
          ) : null}
          {canOpen ? (
            <Button variant={showProceed ? "secondary" : "primary"} onClick={onOpen} disabled={opening}>
              {opening ? "Opening SureLC…" : "Open SureLC"}
            </Button>
          ) : null}
        </div>
      </div>

      <ol className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Contracting steps">
        {steps.map((step) => (
          <li
            key={step.id}
            className="rounded-xl border border-border-default bg-surface-overlay p-3"
          >
            <div className="flex items-center gap-2">
              <span
                className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-surface-raised text-sm font-bold text-accent"
                aria-hidden
              >
                {stepMark(step.status)}
              </span>
              <p className="text-sm font-bold text-content-primary">{step.label}</p>
            </div>
            <p className="mt-2 text-xs leading-5 text-content-secondary">{step.detail}</p>
          </li>
        ))}
      </ol>

      {onboarding.producerId ? (
        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-bold text-content-primary">What SureLC still needs</h3>
            <p className="mt-1 text-xs text-content-secondary">
              SureLC will not send a carrier request until the items in red are done. Picking carriers on Altura
              is not blocked by them.
            </p>
          </div>
          <SurelcReadinessList items={onboarding.readiness} />
        </div>
      ) : null}

      {onboarding.producerId ? (
        <div className="space-y-2">
          <h3 className="text-sm font-bold text-content-primary">Your requests in SureLC</h3>
          <SurelcRequestList requests={onboarding.requests} />
          {onboarding.proceededAt ? (
            <p className="text-xs text-content-muted">
              Setup is unlocked. SureLC stays one click away here for the rest of your requests.
            </p>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <p className="text-sm text-status-rejected-text" role="alert">
          {error}
        </p>
      ) : null}
    </Card>
  );
}
